import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, ArrowRightLeft } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CounterOfferThread } from './CounterOfferThread';
import { useCounterOffers } from '@/hooks/useCounterOffers';

interface CounterOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealApplicationId: string;
  dealId: string;
  currentValue: number;
  currentDealType: string;
  currentDeadline?: string;
  onSuccess?: () => void;
}

export const CounterOfferModal: React.FC<CounterOfferModalProps> = ({
  isOpen,
  onClose,
  dealApplicationId,
  dealId,
  currentValue,
  currentDealType,
  currentDeadline,
  onSuccess,
}) => {
  const [proposedValue, setProposedValue] = useState(currentValue);
  const [proposedDealType, setProposedDealType] = useState(currentDealType);
  const [proposedDeadline, setProposedDeadline] = useState<Date | undefined>(
    currentDeadline ? new Date(currentDeadline) : undefined
  );
  const [proposedTerms, setProposedTerms] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    counterOffers,
    loading,
    fetchCounterOffers,
    submitCounterOffer,
    respondToCounterOffer,
    getLatestPendingOffer,
  } = useCounterOffers();

  useEffect(() => {
    if (isOpen && dealApplicationId) {
      fetchCounterOffers(dealApplicationId);
    }
  }, [isOpen, dealApplicationId, fetchCounterOffers]);

  // Reset form when opening with new values
  useEffect(() => {
    if (isOpen) {
      setProposedValue(currentValue);
      setProposedDealType(currentDealType);
      setProposedDeadline(currentDeadline ? new Date(currentDeadline) : undefined);
      setProposedTerms('');
      setMessage('');
    }
  }, [isOpen, currentValue, currentDealType, currentDeadline]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const result = await submitCounterOffer({
      dealApplicationId,
      dealId,
      proposedValue,
      proposedDealType,
      proposedDeadline: proposedDeadline ? format(proposedDeadline, 'yyyy-MM-dd') : undefined,
      proposedTerms: proposedTerms || undefined,
      message: message || undefined,
    });
    setSubmitting(false);

    if (result.success) {
      onSuccess?.();
    }
  };

  const handleAccept = async (counterId: string) => {
    const result = await respondToCounterOffer(counterId, 'accepted', dealApplicationId);
    if (result.success) {
      onSuccess?.();
      onClose();
    }
  };

  const handleReject = async (counterId: string) => {
    await respondToCounterOffer(counterId, 'rejected', dealApplicationId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Negotiate Deal Terms
          </DialogTitle>
        </DialogHeader>

        {/* Negotiation History */}
        {counterOffers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Negotiation History</h4>
            <div className="max-h-[250px] overflow-y-auto">
              <CounterOfferThread
                counterOffers={counterOffers}
                onAccept={handleAccept}
                onReject={handleReject}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Counter Offer Form */}
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-medium">Propose New Terms</h4>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="proposed-value">Proposed Value ($)</Label>
              <Input
                id="proposed-value"
                type="number"
                value={proposedValue}
                onChange={(e) => setProposedValue(Number(e.target.value))}
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label>Deal Type</Label>
              <Select value={proposedDealType} onValueChange={setProposedDealType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sponsorship">Sponsorship</SelectItem>
                  <SelectItem value="endorsement">Endorsement</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                  <SelectItem value="appearance">Appearance</SelectItem>
                  <SelectItem value="ambassador">Ambassador</SelectItem>
                  <SelectItem value="collaboration">Collaboration</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Proposed Deadline</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !proposedDeadline && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {proposedDeadline ? format(proposedDeadline, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={proposedDeadline}
                  onSelect={setProposedDeadline}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="proposed-terms">Additional Terms</Label>
            <Textarea
              id="proposed-terms"
              placeholder="Describe any specific terms or conditions..."
              value={proposedTerms}
              onChange={(e) => setProposedTerms(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Add a message to explain your proposal..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || proposedValue <= 0}>
            {submitting ? 'Sending...' : 'Send Counter Offer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
